import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { dashboardAPI } from '../../services/api';
import AdminLayout from '../../components/AdminLayout';
import { motion } from 'framer-motion';
import { DollarSign, ShoppingCart, Users, Truck, TrendingUp, AlertCircle } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

export default function Dashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const chartData = stats?.weekly_chart?.length ? stats.weekly_chart : [];
  const hasSalesData = chartData.some(d => d.sales > 0);
  const revenue = stats?.month_sales || 0;
  const expenses = stats?.month_expenses || 0;

  const expensePercent = revenue > 0 ? Math.min((expenses / revenue) * 100, 100) : 0;
  const profitPercent = revenue > 0 ? Math.max(((revenue - expenses) / revenue) * 100, 0) : 0;

  useEffect(() => { fetchStats(); }, []);

  const fetchStats = async () => {
    try {
      const { data } = await dashboardAPI.getStats();
      setStats(data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <AdminLayout activeItem="Dashboard">
      <header className="bg-white border-b border-slate-200 px-8 py-4" data-testid="admin-header">
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-medium text-brand-primary font-outfit">Dashboard</h1>
          <div className="flex items-center space-x-4">
            <div className="text-right">
              <p className="text-sm font-medium text-brand-primary" data-testid="user-name">{user?.name}</p>
              <p className="text-xs text-slate-500" data-testid="user-role">{user?.role?.replace('_', ' ').toUpperCase()}</p>
              {user?.franchise_name && (
                <p className="text-xs text-brand-accent" data-testid="franchise-name">{user.franchise_name}</p>
              )}
            </div>
          </div>
        </div>
      </header>

      <main className="p-8" data-testid="admin-dashboard">
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-accent"></div>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
                className="bg-white p-6 border border-slate-200 hover:shadow-md transition-all duration-200" data-testid="stat-today-sales">
                <div className="flex items-center justify-between mb-4">
                  <span className="text-2xl font-bold text-brand-accent">PKR</span>
                  <TrendingUp className="h-5 w-5 text-green-500" />
                </div>
                <p className="text-sm text-slate-600 mb-1">Today's Sales</p>
                <p className="text-3xl font-bold text-brand-primary">Rs. {stats?.today_sales?.toLocaleString() || 0}</p>
                <p className="text-xs text-slate-500 mt-2">{stats?.today_orders || 0} orders</p>
              </motion.div>

              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.1 }}
                className="bg-white p-6 border border-slate-200 hover:shadow-md transition-all duration-200" data-testid="stat-month-sales">
                <div className="flex items-center justify-between mb-4">
                  <ShoppingCart className="h-8 w-8 text-brand-accent" />
                </div>
                <p className="text-sm text-slate-600 mb-1">Monthly Sales</p>
                <p className="text-3xl font-bold text-brand-primary">Rs. {stats?.month_sales?.toLocaleString() || 0}</p>
                <p className="text-xs text-slate-500 mt-2">{stats?.month_orders || 0} orders</p>
              </motion.div>

              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.2 }}
                className="bg-white p-6 border border-slate-200 hover:shadow-md transition-all duration-200" data-testid="stat-customers">
                <div className="flex items-center justify-between mb-4">
                  <Users className="h-8 w-8 text-brand-accent" />
                </div>
                <p className="text-sm text-slate-600 mb-1">Total Customers</p>
                <p className="text-3xl font-bold text-brand-primary">{stats?.total_customers || 0}</p>
              </motion.div>

              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.3 }}
                className="bg-white p-6 border border-slate-200 hover:shadow-md transition-all duration-200" data-testid="stat-pending-deliveries">
                <div className="flex items-center justify-between mb-4">
                  <Truck className="h-8 w-8 text-brand-accent" />
                  {stats?.pending_deliveries > 0 && <AlertCircle className="h-5 w-5 text-orange-500" />}
                </div>
                <p className="text-sm text-slate-600 mb-1">Pending Deliveries</p>
                <p className="text-3xl font-bold text-brand-primary">{stats?.pending_deliveries || 0}</p>
              </motion.div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.4 }}
                className="bg-white p-6 border border-slate-200" data-testid="sales-chart">
                <h3 className="text-lg font-medium text-brand-primary mb-6">Weekly Sales</h3>
                {!hasSalesData ? (
                  <div className="flex items-center justify-center h-[300px] text-slate-500">
                    No sales data for this week
                  </div>
                ) : (
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={chartData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                      <XAxis dataKey="name" stroke="#64748b" />
                      <YAxis stroke="#64748b" />
                      <Tooltip />
                      <Bar dataKey="sales" fill="#00B4D8" />
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </motion.div>

              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.5 }}
                className="bg-white p-6 border border-slate-200" data-testid="revenue-summary">
                <h3 className="text-lg font-medium text-brand-primary mb-6">Monthly Summary</h3>
                <div className="space-y-6">
                  <div>
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-sm text-slate-600">Total Revenue</span>
                      <span className="text-lg font-bold text-brand-primary">Rs. {stats?.month_sales?.toLocaleString() || 0}</span>
                    </div>
                    <div className="w-full bg-slate-100 h-2">
                      <div className="bg-brand-accent h-2" style={{ width: '100%' }}></div>
                    </div>
                  </div>
                  <div>
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-sm text-slate-600">Total Expenses</span>
                      <span className="text-lg font-bold text-red-600">Rs. {stats?.month_expenses?.toLocaleString() || 0}</span>
                    </div>
                    <div className="w-full bg-slate-100 h-2">
                      <div className="bg-red-500 h-2" style={{ width: `${expensePercent}%` }}></div>
                    </div>
                  </div>
                  <div className="pt-4 border-t border-slate-200">
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-sm text-slate-600">Net Profit</span>
                      <span className="text-2xl font-bold text-green-600">
                        Rs. {(revenue - expenses).toLocaleString()}
                      </span>
                    </div>
                    <div className="w-full bg-slate-100 h-2">
                      <div className="bg-green-500 h-2" style={{ width: `${profitPercent}%` }}></div>
                    </div>
                  </div>
                </div>
              </motion.div>
            </div>
          </>
        )}
      </main>
    </AdminLayout>
  );
}
